import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { cn } from "@/lib/utils";

function isTouchDevice() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

const HoverCard = ({
  children,
  openDelay = 200,
  closeDelay = 100,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...rest
}: React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root>) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);

  const open = controlledOpen ?? internalOpen;
  const onOpenChange = controlledOnOpenChange ?? setInternalOpen;

  React.useEffect(() => {
    setIsTouch(isTouchDevice());
  }, []);

  return (
    <HoverCardPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      openDelay={isTouch ? 0 : openDelay}
      closeDelay={closeDelay}
      {...rest}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        // Check if it's the Trigger component
        if (
          // @ts-ignore
          child.type?.displayName === "HoverCardTrigger" ||
          // @ts-ignore
          child.type === HoverCardTrigger
        ) {
          return React.cloneElement(child as React.ReactElement<any>, {
            onClick: (e: React.MouseEvent) => {
              if (isTouch) {
                onOpenChange(!open);
              }
              // Call original onClick if present
              child.props.onClick?.(e);
            },
          });
        }

        return child;
      })}
    </HoverCardPrimitive.Root>
  );
};

const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(({ children, ...props }, ref) => {
  return (
    <HoverCardPrimitive.Trigger ref={ref} {...props}>
      {children}
    </HoverCardPrimitive.Trigger>
  );
});
HoverCardTrigger.displayName = "HoverCardTrigger";

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Portal>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-xs text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent };
